/**
 * EIUpload — single + bulk PDF upload, drag-drop, progress + processing status.
 * Phase 2 adds:
 *   - Status selector (Active / Good Leaver / Bad Leaver) applied to every
 *     person extracted from the uploaded file(s).
 *   - Multi-person "Team Graph" PDFs are detected server-side and produce N
 *     profile records per file — progress reports {pages_done, people_done}.
 */

import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Upload, FileText, Loader2, CheckCircle2, AlertCircle, ExternalLink,
  UserCheck, UserMinus, UserX, Sparkles,
} from "lucide-react";
import { API, fmtDate, toast } from "../EmploymentIntelligence";

const STATUS_OPTIONS = [
  { value: "active",       label: "Active",       Icon: UserCheck, color: "#10b981",
    blurb: "Currently employed — onboarding, performance, team mapping." },
  { value: "good_leaver",  label: "Good leaver",  Icon: UserMinus, color: "#0ea5e9",
    blurb: "Left in good circumstances — career step, retirement, life event." },
  { value: "bad_leaver",   label: "Bad leaver",   Icon: UserX,     color: "#e11d48",
    blurb: "Left in poor circumstances — turnover, dismissal, attrition risk to learn from." },
];

export default function EIUpload({ onComplete }) {
  const [dragOver, setDragOver] = useState(false);
  const [ingestStatus, setIngestStatus] = useState("active");
  const [jobs, setJobs] = useState([]);
  const [pdfs, setPdfs] = useState([]);
  const fileInputRef = useRef(null);
  const pollersRef = useRef({});

  useEffect(() => {
    loadPdfs();
    loadRecentJobs();
    return () => {
      Object.values(pollersRef.current).forEach((id) => clearInterval(id));
      pollersRef.current = {};
    };
  }, []);

  async function loadPdfs() {
    try {
      const res = await axios.get(`${API}/employment-intelligence/pdfs`);
      setPdfs(res.data || []);
    } catch { /* ignore */ }
  }

  async function loadRecentJobs() {
    try {
      const res = await axios.get(`${API}/employment-intelligence/jobs/recent?limit=12`);
      const recent = (res.data || []).map((j) => ({ ...j, _t: Date.now() }));
      setJobs(recent);
      // Resume polling any unfinished ones
      recent
        .filter((j) => j.status !== "done" && j.status !== "error")
        .forEach((j) => pollJob({ ...j, job_id: j.id }));
    } catch { /* ignore */ }
  }

  const pollJob = (job) => {
    const job_id = job.job_id || job.id;
    if (!job_id || pollersRef.current[job_id]) return;
    const id = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/employment-intelligence/jobs/${job_id}`);
        const j = res.data;
        setJobs((prev) => prev.map((p) => (p.id === job_id || p.job_id === job_id) ? { ...p, ...j } : p));
        if (j.status === "done") {
          clearInterval(id);
          delete pollersRef.current[job_id];
          const peeps = j.people_count || j.profile_ids?.length || 1;
          toast.success(`Extracted ${peeps} ${peeps === 1 ? "person" : "people"} from ${job.filename}`);
          loadPdfs();
          // Open the first profile if it was a single-person report
          if (j.report_kind === "single") onComplete?.(j.profile_id);
          else onComplete?.();
        } else if (j.status === "error") {
          clearInterval(id);
          delete pollersRef.current[job_id];
          toast.error(`${job.filename} failed: ${j.error || "extraction error"}`);
        }
      } catch { /* keep polling */ }
    }, 3000);
    pollersRef.current[job_id] = id;
  };

  const uploadOne = async (file) => {
    if (!file || file.type !== "application/pdf") {
      toast.error(`${file?.name || "file"} is not a PDF`);
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("employment_status", ingestStatus);
    try {
      const res = await axios.post(`${API}/employment-intelligence/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const job = {
        ...res.data,
        id: res.data.job_id,
        filename: file.name,
        status: "queued",
        stage: "queued",
        ingest_status: ingestStatus,
        _t: Date.now(),
      };
      setJobs((prev) => [job, ...prev]);
      pollJob(job);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not upload ${file.name}`);
    }
  };

  const onFilesChosen = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type === "application/pdf");
    if (files.length === 0) return;
    toast.success(`Uploading ${files.length} ${files.length === 1 ? "file" : "files"} as ${ingestStatus.replace("_", " ")}…`);
    await Promise.all(files.map((f) => uploadOne(f)));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    onFilesChosen(e.dataTransfer.files);
  };

  const triggerAutoIngest = async () => {
    try {
      await axios.post(`${API}/employment-intelligence/auto-ingest-demo`);
      toast.success("Demo ingestion queued — 4 PDFs will appear here as they extract");
      setTimeout(loadRecentJobs, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start demo ingestion");
    }
  };

  const activeOption = STATUS_OPTIONS.find((s) => s.value === ingestStatus);

  return (
    <>
      <div className="ei-section-head">
        <div>
          <h2>PDF Library &amp; Ingestion</h2>
          <p>Pick the type, then drop in DISC PDFs. We support single-person reports and multi-person Team Graphs — each person gets their own profile.</p>
        </div>
        <span className="ei-card-sub">{pdfs.length} {pdfs.length === 1 ? "file" : "files"} stored</span>
      </div>

      {/* Ingest status selector */}
      <div className="ei-upload-status-row" data-testid="ei-ingest-status-row">
        {STATUS_OPTIONS.map(({ value, label, Icon, color, blurb }) => {
          const on = ingestStatus === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => setIngestStatus(value)}
              data-testid={`ei-ingest-${value}`}
              className={`ei-status-card ${on ? "is-on" : ""}`}
              style={{ "--accent": color }}
            >
              <div className="ei-status-card-icon"><Icon size={16} /></div>
              <div>
                <strong>{label}</strong>
                <span>{blurb}</span>
              </div>
              {on && <span className="ei-status-card-tick">●</span>}
            </button>
          );
        })}
      </div>

      {/* Dropzone */}
      <div
        className={`ei-dropzone ${dragOver ? "is-drag" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        data-testid="ei-dropzone"
        style={{ "--accent": activeOption.color }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          multiple
          style={{ display: "none" }}
          onChange={(e) => onFilesChosen(e.target.files)}
          data-testid="ei-file-input"
        />
        <div className="ei-dropzone-icon" style={{ background: `${activeOption.color}1a`, color: activeOption.color }}>
          <Upload size={26} />
        </div>
        <h3>Drop DISC PDF reports here</h3>
        <p>Files will be tagged as <strong style={{ color: activeOption.color }}>{activeOption.label}</strong> · single or multi-person Team Graphs · up to 25 MB each</p>
      </div>

      <button
        type="button"
        className="ei-secondary"
        onClick={triggerAutoIngest}
        data-testid="ei-auto-ingest"
        style={{ marginTop: 14, alignSelf: "flex-start" }}
      >
        <Sparkles size={13} /> Re-run NicheCom demo ingestion (4 leaver PDFs)
      </button>

      {/* Active jobs */}
      {jobs.length > 0 && (
        <section className="ei-card" style={{ marginTop: 22 }}>
          <div className="ei-card-head">
            <h3 className="ei-card-title">Extraction queue</h3>
            <span className="ei-card-sub">{jobs.filter((j) => j.status !== "done" && j.status !== "error").length} active</span>
          </div>
          <div className="ei-jobs">
            {jobs.map((j) => {
              const peeps = j.people_count || j.people_done || j.profile_ids?.length || 0;
              const tagColor = j.ingest_leaver_type === "good" ? "#0ea5e9"
                : j.ingest_leaver_type === "bad" ? "#e11d48" : "#10b981";
              const tag = j.ingest_leaver_type === "good" ? "Good leaver"
                : j.ingest_leaver_type === "bad" ? "Bad leaver" : "Active";
              return (
                <div key={j.id || j.job_id} className="ei-job">
                  {j.status === "done" && <CheckCircle2 size={18} color="#10b981" />}
                  {j.status === "error" && <AlertCircle size={18} color="#e11d48" />}
                  {(j.status === "queued" || j.status === "running") && <Loader2 size={16} className="ei-spin" color="#1a472a" />}
                  <div>
                    <strong style={{ fontSize: 13, color: "#0d1b2a" }}>{j.filename}</strong>
                    <div className="ei-job-meta">
                      <span style={{ color: tagColor, fontWeight: 700 }}>{tag}</span>
                      {" · "}
                      {j.status === "done" && (peeps ? `${peeps} ${peeps === 1 ? "person" : "people"} extracted` : "Extracted")}
                      {j.status === "error" && (j.error || "Extraction failed")}
                      {j.status === "running" && (
                        <>
                          {j.stage === "vision_extraction" && `Reading page ${j.pages_done || 0}/${j.pages_total || "?"}…`}
                          {j.stage === "writing_profiles" && `Analysing person ${j.people_done || 0}/${j.people_total || "?"}…`}
                          {j.stage === "ai_extraction" && "Extracting with AI…"}
                          {j.stage === "rendering_pages" && "Rendering PDF pages…"}
                          {j.stage === "preparing" && "Preparing…"}
                          {j.stage === "downloading" && "Downloading PDF…"}
                          {!["vision_extraction", "writing_profiles", "ai_extraction", "rendering_pages", "preparing", "downloading"].includes(j.stage) && "Processing…"}
                        </>
                      )}
                      {j.status === "queued" && "Queued"}
                    </div>
                  </div>
                  <span className={`ei-job-status is-${j.status}`}>{j.status}</span>
                  {j.status === "done" && j.profile_id && (
                    <button className="ei-secondary" onClick={() => onComplete?.(j.profile_id)}>
                      Open profile <ExternalLink size={12} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* PDF library */}
      <section className="ei-card" style={{ marginTop: 22 }}>
        <div className="ei-card-head">
          <h3 className="ei-card-title">PDF library</h3>
          <span className="ei-card-sub">Every uploaded file, newest first</span>
        </div>
        {pdfs.length === 0 ? (
          <div style={{ padding: 20, color: "#6a707d", fontSize: 13, textAlign: "center" }}>
            No files yet. Upload a PDF above to get started.
          </div>
        ) : (
          <table className="ei-table">
            <thead>
              <tr>
                <th>Filename</th>
                <th>Type</th>
                <th>People</th>
                <th>Status</th>
                <th>Uploaded</th>
              </tr>
            </thead>
            <tbody>
              {pdfs.map((p) => {
                const tagColor = p.ingest_leaver_type === "good" ? "#0ea5e9"
                  : p.ingest_leaver_type === "bad" ? "#e11d48" : "#10b981";
                const tag = p.ingest_leaver_type === "good" ? "Good leaver"
                  : p.ingest_leaver_type === "bad" ? "Bad leaver" : "Active";
                return (
                  <tr key={p.id} style={{ cursor: "default" }}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <FileText size={14} color="#6a707d" /> {p.uploaded_filename}
                      </div>
                    </td>
                    <td>
                      <span style={{ color: tagColor, fontWeight: 700, fontSize: 12 }}>{tag}</span>
                      {p.report_kind === "team" && <span className="ei-pill-mini" style={{ marginLeft: 6 }}>Team</span>}
                    </td>
                    <td>{p.people_count || (p.profile_id ? 1 : "—")}</td>
                    <td>
                      <span className={`ei-job-status is-${p.status === "processed" ? "done" : p.status}`}>
                        {p.status}
                      </span>
                    </td>
                    <td>{fmtDate(p.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}
